import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps, cn } from "@tellescope/ui";
import { useTellescopeMutation } from "@tellescope/tanstack-query";
import microsoftLogo from "../../assets/ms-logo.svg";
import oktaLogo from "../../assets/okta-logo.png";
import { i18n } from "@tellescope/i18n";

export type SSOProvider = "microsoft-oidc" | "okta-oidc";

const getProviderConfig = (): Record<SSOProvider, { logoSrc: string; label: string; alt: string }> => ({
  "microsoft-oidc": {
    logoSrc: microsoftLogo,
    label: i18n.t("Sign in with Microsoft", { ns: "auth" }),
    alt: "Microsoft",
  },
  "okta-oidc": {
    logoSrc: oktaLogo,
    label: i18n.t("Sign in with Okta", { ns: "auth" }),
    alt: "Okta",
  },
});

interface SSOLoginButtonProps extends Omit<ButtonProps, "onClick" | "disabled"> {
  provider: SSOProvider;
  configurationId?: string;
  redirectUrl?: string;
  onBeforeRedirect?: () => void;
}

export const SSOLoginButton = ({
  provider,
  configurationId,
  redirectUrl,
  onBeforeRedirect,
  className,
  ...rest
}: SSOLoginButtonProps) => {
  const { t } = useTranslation("auth");
  const config = getProviderConfig()[provider];
  const [isRedirecting, setIsRedirecting] = useState(false);

  const beginSSOMutation = useTellescopeMutation({
    model: "users",
    method: "begin_sso",
    autoCacheUpdateStrategy: "disabled",
    onSuccess: (result) => {
      window.location.href = result.url;
    },
  });

  const handleClick = () => {
    setIsRedirecting(true);
    onBeforeRedirect?.();
    beginSSOMutation.mutate({
      provider,
      configurationId,
      redirectUrl: redirectUrl ?? window.location.origin,
    });
  };

  return (
    <Button
      variant="outline"
      className={cn(
        "relative flex-1 flex items-center justify-center pr-3 pl-[40px] rounded-[4px] h-[38px] border-[#dadce0] text-gray-700 w-full",
        className
      )}
      onClick={handleClick}
      disabled={isRedirecting}
      {...rest}
    >
      <img src={config.logoSrc} alt={config.alt} className="absolute left-3 h-5" />
      <span>{isRedirecting ? t("Redirecting...") : config.label}</span>
    </Button>
  );
};
